import React, { createContext, useState, useContext } from 'react';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage for non-guest users.
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // If a guest user was stored from a previous session, remove it.
      if (parsed.email && parsed.email.toLowerCase() === 'guest@anonymous') {
        localStorage.removeItem('user');
      } else {
        return parsed;
      }
    }
    // If no stored user, check URL for guest flag.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('user') === 'guest') {
      return { name: 'Guest', email: 'guest@anonymous' };
    }
    return null;
  });

  // Login stores the full user data (including tokens)
  const login = (userData) => {
    setUser(userData);
    // Persist only if not a guest.
    if (userData.email && userData.email.toLowerCase() !== 'guest@anonymous') {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    // Optionally remove guest tokens as well.
    localStorage.removeItem('guestToken');
    localStorage.removeItem('guestRefreshToken');
  };

  // Refresh access token using the refresh token stored in user data.
  const refreshAccessToken = async () => {
    if (!user || !user.refresh_token) {
      throw new Error("No refresh token available");
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: user.refresh_token })
      });
      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }
      const data = await response.json();
      const updatedUser = {
        ...user,
        token: data.access_token,
        refresh_token: data.refresh_token
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser.token;
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout(); // Log out if refresh fails.
      throw error;
    }
  };

  // Fetch guest token from the guest endpoint and store it in localStorage.
  const fetchGuestToken = async (guestId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/guest/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestId })
      });
      if (!response.ok) {
        throw new Error("Failed to fetch guest token");
      }
      const data = await response.json();
      // Store guest tokens for authFetch to pick up.
      localStorage.setItem('guestToken', data.token);
      localStorage.setItem('guestRefreshToken', data.refresh_token);
      return data;
    } catch (error) {
      console.error("Error fetching guest token:", error);
      throw error;
    }
  };

  // Custom fetch that attaches the access token and handles token refresh on 401 errors.
  const authFetch = async (url, options = {}) => {
    try {
      // Attach Authorization header if a logged-in user token is available.
      if (user && user.token) {
        options.headers = {
          ...(options.headers || {}),
          'Authorization': `Bearer ${user.token}`,
        };
      } else {
        // For guest users, retrieve guest token from localStorage.
        const guestToken = localStorage.getItem('guestToken');
        if (guestToken) {
          options.headers = {
            ...(options.headers || {}),
            'Authorization': `Bearer ${guestToken}`,
          };
        }
      }

      let response = await fetch(url, options);

      // If unauthorized, attempt to refresh token and retry once.
      if (response.status === 401) {
        try {
          const newToken = await refreshAccessToken();
          options.headers['Authorization'] = `Bearer ${newToken}`;
          response = await fetch(url, options);
        } catch (error) {
          logout();
          throw new Error("Unauthorized: Token refresh failed. Please log in again.");
        }
      }

      // If response is not ok, extract and throw an error message.
      if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response;
    } catch (error) {
      console.error("authFetch error:", error);
      throw error;
    }
  };

  const value = { user, login, logout, refreshAccessToken, authFetch, fetchGuestToken };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
